import { useMasterDataStore, selectAppSettings } from '@store/master-data.store';
import type { AppSettingsItem } from '@models/master-data/master-data.model';
import { resolveAppSettingsForHost } from '@utils/app-settings-resolver.util';
import { sessionStorageUtil } from '@utils/session-storage.util';
import { getAppSettingKey } from '@utils/session-keys.constants';

/**
 * Reads a single field from APP_SETTINGS, resolved for the current host.
 * The resolved value is cached per-key in sessionStorage so it survives until
 * fresh master data loads (e.g. across a hard refresh mid-session), falling
 * back to `fallback` when neither is available yet.
 */
export function useAppSetting<K extends keyof AppSettingsItem>(
  key: K,
  fallback: NonNullable<AppSettingsItem[K]>,
): NonNullable<AppSettingsItem[K]> {
  const appSettings = useMasterDataStore(selectAppSettings);
  const resolved = resolveAppSettingsForHost(appSettings);
  const liveValue = resolved[key];

  if (liveValue !== undefined) {
    sessionStorageUtil.set(getAppSettingKey(key), liveValue);
    return liveValue as NonNullable<AppSettingsItem[K]>;
  }

  const cachedValue = sessionStorageUtil.get<AppSettingsItem[K]>(getAppSettingKey(key));
  return cachedValue ?? fallback;
}
